/** @format */

import {
	Component,
	ElementRef,
	EventEmitter,
	Inject,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { chain } from '@core/chain';
import { DeviceService, Scene7Service, UtilityService, WINDOW } from '@shared/utils/services';
import { Logger } from '@logger';
import { ProductAnalyticsService, ProductService, Store } from '@shared/data-access/services';
import { DefiningAttributeName } from '@pdp/data-access/enums';
import { HeaderStoreSelection, KillSwitches, ProductSku } from '@shared/data-access/interfaces';
import { first } from 'rxjs/operators';

@Component({
	selector: 'pdp-product-availability-notification-form',
	templateUrl: './product-availability-notification-form.component.html',
	styleUrls: ['./product-availability-notification-form.component.scss'],
	standalone: false
})
export class ProductAvailabilityNotificationFormComponent implements OnInit, OnChanges {
	/**
	 * @description Element references needed for analytics
	 */
	@ViewChild('atsNotificationCheckbox', { static: false }) atsNotificationCheckBoxRef: ElementRef;
	@ViewChild('bopisNotificationCheckbox', { static: false })
	bopisNotificationCheckBoxRef: ElementRef;

	/**
	 * @description sku - used for analytics, product information and in stock reminder API call
	 */
	@Input() sku: ProductSku = null;

	/**
	 * @description productTitle - Displayed in template
	 */
	@Input() productTitle = '';

	/**
	 * @description productECode - Used in analytics and in stock reminder API call
	 */
	@Input() productECode = '';

	/**
	 * @description storeDetails - Used in stock reminder API call
	 */
	@Input() storeDetails?: HeaderStoreSelection = null;

	/**
	 * @description current selected shipping mode
	 */
	@Input() shipMode: string;

	/**
	 * @description panelClosed - Triggered when 'Cancel' button is clicked, prompting parent component to close side panel
	 */
	@Output() panelClosed: EventEmitter<void> = new EventEmitter<void>();

	/**
	 * @description notifyUserForm - Form used to collect user's email and notification preference
	 */
	public notifyUserForm: UntypedFormGroup;

	/**
	 * @description submitAttempted - Using this to show form requirement errors on off chance that a user clicks the submit button when the form is invalid
	 */
	public submitAttempted: boolean;

	/**
	 * @description showSuccessMsg - Determine whether we should show the success message
	 */
	public showSuccessMsg: boolean;

	/**
	 * @description showErrorMsg - Determine whether we should show the error message
	 */
	public showErrorMsg: boolean;

	/**
	 * @description attributeDisplay - String containing the products attributes, is displayed in the template
	 */
	public attributeDisplay: string;

	/**
	 * @description labelValuesAttributes - Array of objects containing key value pairs label and value, used for in stock reminder API call
	 */
	private labelValuesAttributes: { label: string; value: string }[];

	/**
	 * @description colorValue - String contaiing the skus color attribute value, used to retrieve product image, which is then used for in stock reminder API call
	 */
	private colorValue = '';

	/**
	 * @description cancelButtonText - Will change depending if the user successfully submits their email and notification preference
	 */
	public cancelButtonText = 'Cancel';

	private analyticEventData: {
		atsNotification?: HTMLInputElement;
		ecode: string;
		shipMode?: string;
		sku: ProductSku;
		store: string;
	};
	public notifyMeGoldMessageEnabled: boolean;
	public isLoadingStateRecsEnabled: boolean;
	protected isNewRecsCardsNotifyMePanelEnabled = false;

	constructor(
		private scene7Service: Scene7Service,
		private analyticsService: ProductAnalyticsService,
		private productService: ProductService,
		private logger: Logger,
		@Inject(WINDOW) protected window: Window,
		private deviceService: DeviceService,
		private store: Store,
		private utilityService: UtilityService
	) {}

	ngOnInit() {
		this.notifyUserForm = new UntypedFormGroup({
			email: new UntypedFormControl('', [Validators.required, Validators.email])
		});
		this.setKillSwitches();
	}

	ngOnChanges(changes: SimpleChanges): void {
		// If we have any sku change then we need to update attributeDisplay, labelValuesAttributes and colorValue
		if (changes.sku) {
			this.runThroughProductAttributes();
		}
		if (!this.notifyUserForm) {
			return;
		}
	}

	get notifyUserFormControls() {
		return this.notifyUserForm.controls;
	}

	private setKillSwitches() {
		this.store
			.select('ENVIRONMENT_KILLSWITCH_VALUES')
			.pipe(first())
			.subscribe((killSwitch: KillSwitches) => {
				this.notifyMeGoldMessageEnabled = this.utilityService.checkKillSwitch(
					'notifyMeSCGoldMessageEnabled',
					killSwitch,
					chain.chainIdentifierAbbr
				);
				this.isNewRecsCardsNotifyMePanelEnabled = this.utilityService.checkKillSwitch(
					'isNewRecsCardsNotifyMePanelEnabled',
					killSwitch,
					chain.chainIdentifierAbbr
				);
				this.isLoadingStateRecsEnabled = this.utilityService.checkKillSwitch(
					'isLoadingStateRecsEnabled',
					killSwitch,
					chain.chainIdentifierAbbr
				);
			});
	}

	public closePanel() {
		this.panelClosed.emit();
	}
	/**
	 * @description Makes API call to set up a in stock reminder for the user
	 */
	public notifyUser(shipMode: string) {
		this.submitAttempted = true;
		this.showSuccessMsg = false;
		this.showErrorMsg = false;

		if (this.notifyUserForm.invalid) {
			return;
		}
		this.analyticEventData = {
			ecode: this.productECode,
			sku: this.sku,
			store: this.storeDetails?.store || this.storeDetails?.location || ''
		};

		const imageUrl = `${this.scene7Service.getProductImageUrl(
			this.productECode,
			'ProductBean',
			this.colorValue,
			undefined,
			{},
			false
		)}?qlt=70&wid=600&fmt=jpeg`;

		let ref = window.location.href;
		if (ref.includes('m2.dickssportinggoods')) {
			ref = ref.replace('m2.dickssportinggoods', 'www.dickssportinggoods');
		}

		const tempRes = {
			email: this.notifyUserFormControls.email.value,
			sku: this.sku?.partNumber,
			title: this.productTitle,
			productURL: `${ref.split('?')[0]}?sku=${this.sku?.partNumber}`,
			imageURL: imageUrl,
			attributes: this.labelValuesAttributes
		};

		if (shipMode === 'shipToMe') {
			this.analyticEventData['shipMode'] = this.shipMode;
			const { email, sku, title, productURL, imageURL, attributes } = tempRes;
			this.productService
				.setProductInStockReminder(
					email,
					sku,
					title,
					productURL,
					imageURL,
					attributes,
					'0',
					null,
					1
				)
				.subscribe({
					next: this.inStockReminderSuccess.bind(this),
					error: this.inStockReminderError.bind(this)
				});
		}

		if (shipMode === 'bopis') {
			this.analyticEventData['shipMode'] = this.shipMode;
			const { email, sku, title, productURL, imageURL, attributes } = tempRes;

			// logging the store number undefined issue
			if (!this.isValidRequest(this.storeDetails)) {
				const notifyAlert = {
					message: 'store_undefined_issue',
					sku,
					productURL,
					storeNumber: this.storeDetails?.store || this.storeDetails?.location,
					storeName: this.storeDetails?.name,
					zip: this.storeDetails?.zipcode || this.storeDetails?.zip,
					browser: this.deviceService.browser,
					browserVersion: this.deviceService.browser_version,
					isMobile: this.deviceService.isMobile(),
					os: this.deviceService.os,
					osVersion: this.deviceService.os_version
				};
				this.logger.log(notifyAlert);
			}

			this.productService
				.setProductInStockReminder(
					email,
					sku,
					title,
					productURL,
					imageURL,
					attributes,
					this.storeDetails?.store || this.storeDetails?.location,
					this.storeDetails?.name,
					1
				)
				.subscribe({
					next: this.inStockReminderSuccess.bind(this),
					error: this.inStockReminderError.bind(this)
				});
		}
	}

	public isValidRequest(storeDetails?: HeaderStoreSelection) {
		if (!storeDetails) {
			return false;
		}
		return storeDetails.location !== undefined || storeDetails.store !== undefined;
	}

	/**
	 * @description Loops through our sku's defAttributes (if there are any) and sets the values for attributeDisplay, labelValuesAttributes and colorValue
	 */
	public runThroughProductAttributes(): void {
		this.attributeDisplay = '';
		this.labelValuesAttributes = [];

		let defAttr = [];

		if (this.sku?.definingAttributes && this.sku?.definingAttributes.length > 0) {
			defAttr = this.sku?.definingAttributes;
		} else {
			defAttr = [];
		}
		if (defAttr && defAttr.length > 0) {
			for (let i = 0; i < defAttr.length; ++i) {
				const attr = defAttr[i];
				this.attributeDisplay += `${attr.name}: ${attr.value}${
					i < defAttr.length - 1 ? ', ' : ''
				}`;
				const attrLabelValue = {
					label: attr.name,
					value: attr.value
				};
				this.labelValuesAttributes.push(attrLabelValue);
				if (
					typeof attr.name === 'string' &&
					attr.name.trim().toLowerCase() === DefiningAttributeName.COLOR
				) {
					this.colorValue = attr.value.replace(/[/ -]/g, '_');
				}
			}
		}
	}
	/**
	 * @description
	 * Show success message after in stock reminder is set
	 * Hide success message after five seconds
	 */
	private inStockReminderSuccess() {
		this.analyticsService.triggerAnalyticsEvent('NotifyMeStock', this.analyticEventData);
		this.showSuccessMsg = true;
		this.cancelButtonText = 'Close';
	}
	/**
	 * @description
	 * Show error message after in stock reminder is set
	 * Hide error message after five seconds
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	private inStockReminderError(err?: Error) {
		this.showErrorMsg = true;
		this.cancelButtonText = 'Cancel';
		setTimeout(() => {
			this.showErrorMsg = false;
		}, 5000);
	}

	get emailValidationState(): 'missing' | 'invalid' | false {
		if (
			(this.notifyUserFormControls.email.touched || this.submitAttempted) &&
			this.notifyUserFormControls.email.errors?.required
		) {
			return 'missing';
		} else if (
			(this.submitAttempted && this.notifyUserFormControls.email.invalid) ||
			(this.notifyUserFormControls.email.touched &&
				this.notifyUserFormControls.email.errors?.email)
		) {
			return 'invalid';
		}

		return false;
	}
}
